import { isAuthenticatedPlayer, type Player } from 'src/types/domain/Player'

/**
 * Whether the player's real balance has loaded.
 *
 * The non-blocking launch flow renders the game before the operator-wallet
 * balance arrives, so there is a brief window where the player is authenticated
 * but `balance.amount` is a placeholder. Betting must be hard-blocked during
 * that window — see {@link canAffordBet}.
 */
export const isBalanceReady = (player: Player): boolean =>
  isAuthenticatedPlayer(player) && player.balanceLoaded

interface CanAffordBetParams {
  balanceLoaded: boolean
  balanceAmount: number
  stake: number
  hasActiveBonus: boolean
}

/**
 * The shared bet-affordability + safety gate used by every bet-placement mode
 * (classic, chip-placement, crash).
 *
 * A bet may NEVER fire against an unknown balance: if the real balance has not
 * loaded yet this returns false regardless of the bonus state (a bonus bet must
 * not bypass the not-loaded block). Once loaded, bonus bets are allowed (they
 * don't draw from the cash balance) and normal bets apply the cash check.
 */
export const canAffordBet = ({
  balanceLoaded,
  balanceAmount,
  stake,
  hasActiveBonus
}: CanAffordBetParams): boolean => {
  if (!balanceLoaded) return false
  if (hasActiveBonus) return true
  return stake <= balanceAmount
}

/**
 * Whether the player's KNOWN balance is too small for this stake — the "insufficient
 * balance" state the panel shows and announces to the host page as `balance:insufficient`.
 *
 * Never true while the real balance is still loading: the placeholder amount is 0, and
 * reporting that as a shortfall told the host page the player was out of money on every
 * launch (`currentBalance: 0`), for a player whose wallet had just not answered yet.
 * Betting is blocked during that window by {@link canAffordBet}; this is only about what
 * is said, not what is allowed.
 */
export const isBalanceInsufficient = ({
  balanceLoaded,
  balanceAmount,
  stake,
  hasActiveBonus
}: CanAffordBetParams): boolean => {
  if (!balanceLoaded) return false
  if (hasActiveBonus) return false
  return stake > balanceAmount
}
